import { useCallback, useEffect, useRef, useState } from "react";
import { apiPost, tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { useAuth } from "../state/AuthContext";
import { AUDIT_COLOR_INFO, FIRST_CALCULATION_TEXT, type AuditWindow } from '../lib/definitions/analysis';
import { colorFor } from '../lib/definitions/palette';
import {
  LOCATION_EMPTY_TEXT, LOCATION_NO_VALUE, LOCATION_TIPS,
  locationFailedText, locationLightTag, locationLimitsLine, locationNumber, locationReasonTags, locationTerrainText, locationWindowLabel,
  type LocationLight, type LocationRow, type LocationTable, type TerrainStatus,
} from "../lib/locationCheck";
import { AuditWindowControls, useAuditHistory } from "./AuditHistory";
import { historyQuery } from "../lib/auditHistory";
import { HoverTip } from "./HoverTip";
import { ResponsiveRecordTable } from "./ResponsiveRecordTable";

/** The traffic light as a swatch and a short tag; no light reads Not Enough Links. */
export function LocationLightTag({ light }: { light: LocationLight }) {
  const { effectiveTheme } = useAppData();
  const info = light ? AUDIT_COLOR_INFO[light] : null;
  return (
    <span className="location-light" data-light={light ?? "none"}>
      {info?.color && (
        <span className="legend-swatch" style={{ background: colorFor(info.color, effectiveTheme), display: "inline-block", marginRight: 4 }} />
      )}
      {locationLightTag(light)}
    </span>
  );
}

export function LocationReasonTags({ row }: { row: { reasons?: string[] } }) {
  const tags = locationReasonTags(row);
  if (tags.length === 0) return <>{LOCATION_NO_VALUE}</>;
  return (
    <span className="location-reasons">
      {tags.map((t) => <span key={t} className="location-reason">{t}</span>)}
    </span>
  );
}

function tipped(label: string, tip: string) {
  return <HoverTip placement="above" text={tip}>{label}</HoverTip>;
}

function TerrainLine({ tiles, onFinished }: { tiles: number; onFinished: () => void }) {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState<TerrainStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => tryApiGet<TerrainStatus>("/api/admin/terrain/status").then((s) => s && setStatus(s)), []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (status?.state !== "running") return;
    const t = window.setInterval(() => { void load(); }, 3000);
    return () => window.clearInterval(t);
  }, [status?.state, load]);

  const start = async () => {
    setBusy(true);
    try {
      setStatus(await apiPost<TerrainStatus>("/api/admin/terrain/download"));
    } catch {
      await load();
    } finally {
      setBusy(false);
    }
  };

  const running = status?.state === "running";
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && status && status.state !== "running") onFinished();
    wasRunning.current = status?.state === "running";
  }, [status, onFinished]);
  return (
    <div className="location-terrain-line">
      <span>{locationTerrainText(status, tiles)}</span>
      {isAdmin && (
        <button type="button" className="rf-link-btn" disabled={busy || running} onClick={start}>
          Download Terrain
        </button>
      )}
    </div>
  );
}

export function LocationCheckTab({
  protocol, windowKey, onWindowChange,
}: { protocol: "meshcore" | "meshtastic"; windowKey: AuditWindow; onWindowChange: (w: AuditWindow) => void }) {
  const { takenAt } = useAuditHistory();
  const [data, setData] = useState<LocationTable | null | "pending" | "unavailable">(null);
  const [reload, setReload] = useState(0);
  const refetch = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    tryApiGet<LocationTable | { pending: true }>(`/api/audit/location-check?protocol=${protocol}&window=${windowKey}${historyQuery(takenAt)}`)
      .then((res) => {
        if (cancelled) return;
        if (!res) setData("unavailable");
        else if ("pending" in res && res.pending) setData("pending");
        else setData(res as LocationTable);
      })
      .catch(() => !cancelled && setData("unavailable"));
    return () => { cancelled = true; };
  }, [protocol, windowKey, takenAt, reload]);

  const table = data && data !== "pending" && data !== "unavailable" ? data : null;
  const columns = [
    { key: "node", header: "Node", mobileLabel: "Node" },
    { key: "role", header: "Role", mobileLabel: "Role" },
    { key: "checked", header: tipped("Links Checked", LOCATION_TIPS.linksChecked), mobileLabel: "Links Checked", className: "num" },
    { key: "failed", header: tipped("Failed", LOCATION_TIPS.failed), mobileLabel: "Failed", className: "num" },
    { key: "worst", header: tipped("Worst Link km", LOCATION_TIPS.worstLink), mobileLabel: "Worst Link km", className: "num" },
    { key: "off", header: tipped("Off Estimate km", LOCATION_TIPS.offEstimate), mobileLabel: "Off Estimate km", className: "num" },
    { key: "light", header: tipped("Status", LOCATION_TIPS.light), mobileLabel: "Status" },
    { key: "reason", header: tipped("Reason", LOCATION_TIPS.reason), mobileLabel: "Reason" },
  ];
  const rows = (table?.rows ?? []).map((r: LocationRow) => ({
    key: r.key,
    cells: {
      node: <a href={`#/nodes/${encodeURIComponent(r.key)}`} className="node-name-link">{r.name}</a>,
      role: r.role,
      checked: r.links_checked,
      failed: locationFailedText(r),
      worst: r.status === "not_enough_links" ? LOCATION_NO_VALUE : locationNumber(r.worst_link_km),
      off: r.status === "not_enough_links" ? LOCATION_NO_VALUE : locationNumber(r.estimate_offset_km),
      light: <LocationLightTag light={r.light} />,
      reason: <LocationReasonTags row={r} />,
    },
  }));

  return (
    <section className="page-section location-tab">
      <div className="node-list-toolbar">
        <AuditWindowControls windowKey={windowKey} onWindowChange={onWindowChange} />
        <span className="location-window-label">Window: {locationWindowLabel(windowKey)}</span>
      </div>
      {data === null && <div className="node-detail-note">Loading…</div>}
      {data === "pending" && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {data === "unavailable" && <div className="node-detail-note">Not available right now.</div>}
      {table && (
        <>
          <TerrainLine tiles={table.terrain_tiles} onFinished={refetch} />
          {rows.length === 0 ? (
            <div className="node-detail-note">{LOCATION_EMPTY_TEXT}</div>
          ) : (
            <ResponsiveRecordTable columns={columns} rows={rows} tableClassName="node-list-table location-table" ariaLabel="Impossible Location" />
          )}
          <div className="location-footnote">
            <span>{locationLimitsLine(table.limits)}</span>
            <a href="#/settings?category=audit&group=location">Settings: Location</a>
          </div>
        </>
      )}
    </section>
  );
}
